/**
 * How an AI suggestion for one of the brand's location / commerce / identity
 * fields is shown in the suggestions table and applied to the form.
 *
 * BrandEditor has two modals with the same fields, and these fields live in
 * shared form objects, so the handlers are built once here for both instead of
 * being written out twice.
 *
 * Every `apply` uses a FUNCTIONAL state update. Accepting several suggestions
 * runs several handlers in one pass, and handlers that share a form object
 * would otherwise each spread the same stale copy and overwrite one another —
 * only the last accepted field would survive.
 */
import type { Dispatch, SetStateAction } from 'react';
import type { BrandStatus } from '../../types';
import { normaliseEcommercePlatform, type BrandCommerceForm } from './BrandCommerceFields';
import {
  BRAND_STATUS_OPTIONS,
  OWNERSHIP_TYPE_OPTIONS,
  isBrandStatus,
  isOwnershipType,
  type BrandIdentityForm,
} from './BrandIdentityFields';
import type { BrandLocationForm } from './BrandLocationFields';

export interface AiFieldHandler {
  label: string;
  currentDisplay: string;
  apply: (value: unknown) => void;
}

export interface BrandAiHandlerDeps {
  location: BrandLocationForm;
  setLocation: Dispatch<SetStateAction<BrandLocationForm>>;
  commerce: BrandCommerceForm;
  setCommerce: Dispatch<SetStateAction<BrandCommerceForm>>;
  identity: BrandIdentityForm;
  setIdentity: Dispatch<SetStateAction<BrandIdentityForm>>;
  status: BrandStatus;
  setStatus: (status: BrandStatus) => void;
  isMicroBrand: boolean | undefined;
  setIsMicroBrand: (value: boolean) => void;
}

const labelOf = (options: { value: string; label: string }[], value: string): string =>
  options.find((option) => option.value === value)?.label ?? '';

const yesNo = (value: boolean | undefined): string =>
  value === undefined ? '' : value ? 'Yes' : 'No';

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

/** Suggestion keys → handlers, for the fields added by the enrichment overhaul. */
export function buildBrandAiHandlers(deps: BrandAiHandlerDeps): Record<string, AiFieldHandler> {
  const { location, setLocation, commerce, setCommerce, identity, setIdentity } = deps;

  const locationText = (
    key: 'city' | 'stateRegion' | 'assemblyLocation' | 'designLocation',
    label: string,
  ): AiFieldHandler => ({
    label,
    currentDisplay: location[key],
    apply: (value) => {
      const text = asText(value);
      if (text) setLocation((prev) => ({ ...prev, [key]: text }));
    },
  });

  return {
    city: locationText('city', 'City'),
    stateRegion: locationText('stateRegion', 'State / region'),
    assemblyLocation: locationText('assemblyLocation', 'Assembly location'),
    designLocation: locationText('designLocation', 'Design location'),
    instagramUrl: {
      label: 'Instagram URL',
      currentDisplay: commerce.instagramUrl,
      apply: (value) => {
        const text = asText(value);
        if (text) setCommerce((prev) => ({ ...prev, instagramUrl: text }));
      },
    },
    ecommercePlatform: {
      label: 'Ecommerce platform',
      currentDisplay: commerce.ecommercePlatform,
      apply: (value) => {
        const platform = normaliseEcommercePlatform(typeof value === 'string' ? value : '');
        if (platform) setCommerce((prev) => ({ ...prev, ecommercePlatform: platform }));
      },
    },
    // Dot-keyed, as the callable returns it and as fieldSources stamps it.
    'founder.name': {
      label: 'Founder',
      currentDisplay: commerce.founderName,
      apply: (value) => {
        const text = asText(value);
        if (text) setCommerce((prev) => ({ ...prev, founderName: text }));
      },
    },
    isMicroBrand: {
      label: 'Microbrand',
      currentDisplay: yesNo(deps.isMicroBrand),
      apply: (value) => {
        if (typeof value === 'boolean') deps.setIsMicroBrand(value);
      },
    },
    ownershipType: {
      label: 'Ownership type',
      currentDisplay: labelOf(OWNERSHIP_TYPE_OPTIONS, identity.ownershipType),
      apply: (value) => {
        if (isOwnershipType(value)) setIdentity((prev) => ({ ...prev, ownershipType: value }));
      },
    },
    status: {
      label: 'Status',
      currentDisplay: labelOf(BRAND_STATUS_OPTIONS, deps.status),
      apply: (value) => {
        if (isBrandStatus(value)) deps.setStatus(value);
      },
    },
    yearRevived: {
      label: 'Year revived',
      currentDisplay: identity.yearRevived,
      apply: (value) => {
        const year = typeof value === 'number' ? String(value) : asText(value);
        if (year && /^\d{4}$/.test(year)) setIdentity((prev) => ({ ...prev, yearRevived: year }));
      },
    },
    predecessorBrand: {
      label: 'Predecessor brand',
      currentDisplay: identity.predecessorBrand,
      apply: (value) => {
        const text = asText(value);
        if (text) setIdentity((prev) => ({ ...prev, predecessorBrand: text }));
      },
    },
  };
}

/**
 * The brand fields the editor already holds, as hints for the enrich call —
 * so the model knows what is settled and what to look for. Blank values are
 * left out rather than sent as empty strings.
 */
export function brandEnrichHints(args: {
  location: BrandLocationForm;
  commerce: BrandCommerceForm;
  identity: BrandIdentityForm;
  status: BrandStatus;
  isMicroBrand: boolean | undefined;
  parentOrg: string;
}): {
  city?: string;
  stateRegion?: string;
  assemblyLocation?: string;
  designLocation?: string;
  instagramUrl?: string;
  ecommercePlatform?: string;
  isMicroBrand?: boolean;
  founder?: { name: string };
  parentOrg?: string;
  status?: BrandStatus;
  yearRevived?: number;
  ownershipType?: string;
  predecessorBrand?: string;
} {
  const { location, commerce, identity } = args;
  const year = /^\d{4}$/.test(identity.yearRevived.trim()) ? Number(identity.yearRevived.trim()) : undefined;
  const hints = {
    city: asText(location.city),
    stateRegion: asText(location.stateRegion),
    assemblyLocation: asText(location.assemblyLocation),
    designLocation: asText(location.designLocation),
    instagramUrl: asText(commerce.instagramUrl),
    ecommercePlatform: asText(commerce.ecommercePlatform),
    isMicroBrand: args.isMicroBrand,
    founder: asText(commerce.founderName) ? { name: commerce.founderName.trim() } : undefined,
    parentOrg: asText(args.parentOrg),
    // 'active' is the default, not a finding: sending it would tell the model
    // the status is settled when nobody has looked.
    status: args.status === 'active' ? undefined : args.status,
    yearRevived: year,
    ownershipType: identity.ownershipType || undefined,
    predecessorBrand: asText(identity.predecessorBrand),
  };
  return Object.fromEntries(Object.entries(hints).filter(([, v]) => v !== undefined));
}
